import { FederatedPointerEvent, IDestroyOptions, Point, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { PiggyResourcePackage } from "../../resources/PiggyResourcePackage";
import { IPiggyController } from "../../controller/IPiggyController";
import { PiggyViewHeaderComponent } from "../header/PiggyViewHeaderComponent";
import { PiggyInfoLockedComponent } from "../piggyinfo/PiggyInfoLockedComponent";
import { PiggyContentView } from "./PiggyContentView";
import { PiggyViewLockedHeaderComponent } from "../header/PiggyViewLockedHeaderComponent";
import { PiggyViewUnlockedHeaderComponent } from "../header/PiggyViewUnlockedHeaderComponent";
import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { gsap } from "gsap";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { IPiggyBankModel } from "../../../../common/models/piggybank/IPiggyBankModel";
import { NoCacheDynamicPackage } from "../../../../common/resources/NoCacheDynamicPackage";
import { Event } from "event-system";
import { Spine, ISkeletonData, ITrackEntry, IEvent } from "pixi-spine";
import { PiggyBreakBoneAnimationResourcePackage } from "../../resources/PiggyBreakBoneAnimationResourcePackage";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";

export class CurrentPiggyContentView extends PiggyContentView {
    private static readonly SHOW_DURATION = 0.6;
    private static readonly MOVE_DURATION = 0.6;
    private static readonly BUTTON_TEXT = 'COLLECT';

    private readonly piggyDynamicResources: NoCacheDynamicPackage;
    private readonly playerModel: IPlayerModel;
    private readonly piggyModel: IPiggyBankModel;
    private readonly showAnimation: gsap.core.Timeline;
    private readonly moveAnimation: gsap.core.Timeline;
    private readonly canvasScaler: ICanvasScaler;
    private readonly commonResources: CommonResourcesPackage;
    private readonly currentState: CurrentPiggyState;
    private readonly moveDistanceOffsetX: number;
    private readonly piggyState: CurrentPiggyState;
    private readonly piggyBreakBoneAnimationResources?: PiggyBreakBoneAnimationResourcePackage;

    private header: PiggyViewHeaderComponent;
    private piggyInfo: PiggyInfoLockedComponent;
    private collectButton: ButtonWithScaleAnimation;
    private piggyBreakBoneAnimation?: Spine;

    public get onCollectButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.collectButton.onUp;
    }

    public constructor(
        piggyResources: PiggyResourcePackage,
        piggyDynamicResources: NoCacheDynamicPackage,
        commonResources: CommonResourcesPackage,
        piggyController: IPiggyController,
        piggyTexture: Texture,
        state: CurrentPiggyState,
        playerModel: IPlayerModel,
        canvasScaler: ICanvasScaler,
        piggyModel: IPiggyBankModel,
        moveDistanceOffsetX: number,
        piggyState: CurrentPiggyState,
        piggyBreakBoneAnimationResources?: PiggyBreakBoneAnimationResourcePackage
    ) {
        super(piggyResources, piggyController, piggyTexture, new Point(35, 420));
        this.piggyDynamicResources = piggyDynamicResources;
        this.commonResources = commonResources;
        this.playerModel = playerModel;
        this.canvasScaler = canvasScaler;
        this.piggyModel = piggyModel;
        this.moveDistanceOffsetX = moveDistanceOffsetX;
        this.piggyState = piggyState;
        this.piggyBreakBoneAnimationResources = piggyBreakBoneAnimationResources;
        this.showAnimation = new gsap.core.Timeline({ paused: true });
        this.moveAnimation = new gsap.core.Timeline({ paused: true });
        this.currentState = state;
        this.build();
        this.layout();
        this.swapChildren(this.header, this.bigPiggy);
        this.initShowAnimation();
        this.initMoveAnimation();
    }

    private build(): void {
        this.header = this.currentHeaderComponent(this.currentState);
        this.addChild(this.header);

        if (this.currentState == CurrentPiggyState.UNLOCKED) {
            this.piggyBreakBoneAnimation = new Spine((this.piggyBreakBoneAnimationResources.assets_views_piggy_break_bone_animation_svin_break_json as any).spineData as ISkeletonData);
            this.piggyBreakBoneAnimation.state.setAnimation(0, 'idle', false);
            this.piggyBreakBoneAnimation.scale.set(1.45);
            this.addChild(this.piggyBreakBoneAnimation);

            this.bigPiggy.visible = false;
            this.collectButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
            this.addChild(this.collectButton);
            const buttonText = new Text(CurrentPiggyContentView.BUTTON_TEXT, new ButtonTextStyle());
            buttonText.position.set(81, 48);
            this.collectButton.defaultView.addChild(buttonText);
        }

        this.piggyInfo = new PiggyInfoLockedComponent(
            this.piggyResources,
            this.piggyDynamicResources,
            this.piggyController,
            this.piggyModel,
            this.currentState === CurrentPiggyState.NEW_PIGGY
        );
        this.piggyInfo.pivot.set(this.piggyInfo.width * 0.5, this.piggyInfo.height * 0.5);
        this.addChild(this.piggyInfo);
    }

    private currentHeaderComponent(state: CurrentPiggyState): PiggyViewHeaderComponent {
        switch (state) {
            case CurrentPiggyState.LOCKED:
                return new PiggyViewLockedHeaderComponent(this.piggyResources, this.playerModel.level.value, this.piggyModel.openAtLevel.value, false);
            case CurrentPiggyState.NEW_PIGGY:
                return new PiggyViewLockedHeaderComponent(this.piggyResources, this.playerModel.level.value, this.piggyModel.openAtLevel.value, true);
            case CurrentPiggyState.UNLOCKED:
                return new PiggyViewUnlockedHeaderComponent(this.piggyResources);
        }
    }

    private layout(): void {
        this.piggyInfo.position.set(100 + this.piggyInfo.width * 0.5, 1149 + this.piggyInfo.height * 0.5);
        if (this.currentState == CurrentPiggyState.UNLOCKED) {
            this.collectButton.position.set(229, 1545);
            this.piggyBreakBoneAnimation.position.set(this.canvasScaler.referenceWidth * 0.5, this.canvasScaler.referenceHeight * 0.5 + 26);
            this.piggyInfo.position.y = 987 + this.piggyInfo.height * 0.5;
        }
    }

    private initShowAnimation(): void {
        const duration = CurrentPiggyContentView.SHOW_DURATION;
        const neededPiggy = this.currentState == CurrentPiggyState.UNLOCKED ? this.piggyBreakBoneAnimation : this.bigPiggy;
        this.showAnimation.fromTo(neededPiggy,
            {
                x: neededPiggy.x - neededPiggy.width * 1.5,
                y: neededPiggy.y + 300,
                angle: -20
            },
            {
                x: neededPiggy.position.x,
                y: neededPiggy.y,
                angle: 0,
                duration: duration,
                ease: "power1.out"
            });
        this.showAnimation.fromTo(this.header, { y: -this.header.height }, { y: this.header.position.y, duration: duration, ease: "power1.out" }, "<5%");
        this.showAnimation.fromTo(this.piggyInfo,
            {
                y: this.canvasScaler.referenceHeight,
                angle: -40,
            },
            {
                y: this.piggyInfo.position.y,
                angle: 0,
                duration: duration,
                ease: "power1.out"
            }, "<");
        this.showAnimation.fromTo(this.piggyInfo.scale, { x: 0, y: 0 }, { x: this.piggyInfo.scale.x, y: this.piggyInfo.scale.y, duration: duration, ease: "power1.out" }, "<");
        if (this.currentState == CurrentPiggyState.UNLOCKED) {
            this.showAnimation.fromTo(this.collectButton, { y: this.canvasScaler.referenceHeight }, { y: this.collectButton.position.y, duration: duration, ease: "power1.out" }, "<25%");
        }
    }

    private initMoveAnimation(): void {
        const duration = CurrentPiggyContentView.MOVE_DURATION;
        const distance = this.canvasScaler.referenceWidth + this.moveDistanceOffsetX;
        this.moveAnimation.to(this.header, { x: this.header.position.x - distance, duration: duration, ease: "power1.inOut" });
        const neededPiggy = this.currentState == CurrentPiggyState.UNLOCKED ? this.piggyBreakBoneAnimation : this.bigPiggy;
        this.moveAnimation.to(neededPiggy, { x: neededPiggy.position.x, duration: duration, ease: "power1.inOut" }, "<");
        this.moveAnimation.to(this.piggyInfo, { x: this.piggyInfo.position.x - distance, duration: duration, ease: "power1.inOut" }, "<");
        if (this.currentState == CurrentPiggyState.UNLOCKED) {
            this.moveAnimation.fromTo(this.collectButton, { x: this.collectButton.position.x }, { x: -distance, duration: duration, ease: "power1.inOut" }, "<");
        }
    }

    public hideRewards(): void {
        this.piggyInfo.hideRewards();
    }

    public async playShowAnimation(): Promise<void> {
        await this.showAnimation.play();
    }

    public async playHideAnimation(): Promise<void> {
        await this.showAnimation.reverse();
    }

    public async playMoveLeftAnimation(): Promise<void> {
        await this.moveAnimation.play();
    }

    public async playMoveRightAnimation(): Promise<void> {
        await this.moveAnimation.reverse();
    }

    public async breakPiggy(): Promise<void> {
        if (this.currentState == CurrentPiggyState.UNLOCKED && this.piggyBreakBoneAnimation) {
            this.piggyBreakBoneAnimation.state.setAnimation(0, 'crash', false);
            const animationPCS = new PromiseCompletionSource<void>();
            this.piggyBreakBoneAnimation.state.addListener({ event: (entry: ITrackEntry, event: IEvent) => { animationPCS.complete() } });
            await animationPCS.result;
            this.piggyBreakBoneAnimation.state.clearListeners();
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.showAnimation.kill();
        this.moveAnimation.kill();
        super.destroy(options);
    }
}

export enum CurrentPiggyState {
    NEW_PIGGY,
    LOCKED,
    UNLOCKED
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 78,
            miterLimit: 0,
            dropShadow: true,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5,
            dropShadowDistance: 3,
            dropShadowAlpha: 0.4
        }));
    }
}
